import { useAuth } from "@auth/AuthContext"
import { SchoolMark } from "@components/SchoolMark"
import { Sidebar } from "@components/Sidebar"
import { Button } from "@components/ui/Button"
import { InternalAccountBanner } from "@components/ui/InternalAccountBanner"
import { useClassrooms } from "@features/classrooms"
import { FeedbackProvider } from "@features/feedback"
import { useGradingCompletionInvalidator } from "@features/grading"
import { Menu, Sparkles } from "lucide-react"
import { useCallback, useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { Outlet, useLocation, useNavigate } from "react-router-dom"

/**
 * Routes where the shell's floating "Generar material" button stands down on a
 * phone (PRD-59).
 *
 * - The roster owns that slot: its "Agregar alumno" button was also `fixed`,
 *   56px, `z-30`, four pixels away, and the shell's won the stacking.
 * - The review queue, settings and the scan tool keep their own primary action
 *   at the bottom of the screen — "Aprobar y siguiente", "Guardar cambios",
 *   the walk's per-row scan button — and the 56px circle sat on top of it
 *   (measured at 375px, Etapa B). Generating a material is never the next step
 *   on those three screens.
 *
 * ponytail: a path list, not a context. The day a page needs to put its OWN
 * button here rather than just clear the slot, the shell should take the
 * button as a prop.
 */
const ROUTES_WITHOUT_SHELL_FAB = [
  /^\/salones\/[^/]+\/alumnos$/,
  /^\/materiales\/[^/]+\/revision$/,
  /^\/configuracion$/,
  /^\/herramientas\/escanear$/,
]

export function AppShell() {
  const { t } = useTranslation("common")
  const { professor, logout } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const classroomsQuery = useClassrooms()
  const classrooms = classroomsQuery.data ?? []
  const [mobileOpen, setMobileOpen] = useState(false)
  const mainRef = useRef<HTMLElement | null>(null)
  const drawerRef = useRef<HTMLDivElement | null>(null)
  const hamburgerRef = useRef<HTMLButtonElement | null>(null)

  const handleLogout = useCallback(() => {
    logout()
    navigate("/login", { replace: true })
  }, [logout, navigate])

  useEffect(() => {
    mainRef.current?.scrollTo({ top: 0, left: 0 })
  }, [location.pathname])

  const closeMobile = useCallback(() => setMobileOpen(false), [])

  /**
   * The drawer is a dialog, and now behaves like one.
   *
   * It used to be a bare `<div>` over an `aria-hidden` scrim: no role, no
   * `aria-modal`, no focus moved in, no focus returned, and Escape did nothing
   * (the only Escape handler in the navigation belonged to the user menu
   * *inside* it). On the product's primary device that left a keyboard or
   * screen-reader user tabbing through the page behind the scrim while the menu
   * covered it — the failure `accessibility.md` §8 exists to prevent.
   */
  useEffect(() => {
    if (!mobileOpen) return
    const drawer = drawerRef.current
    const trigger = hamburgerRef.current
    const focusables = () =>
      Array.from(
        drawer?.querySelectorAll<HTMLElement>(
          'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ) ?? [],
      )
    // Defer one frame so the drawer has painted before focus moves.
    const handle = window.requestAnimationFrame(() => focusables()[0]?.focus())

    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setMobileOpen(false)
        return
      }
      if (e.key !== "Tab") return
      const items = focusables()
      if (items.length === 0) return
      const first = items[0]!
      const last = items[items.length - 1]!
      const active = document.activeElement
      if (e.shiftKey && (active === first || !drawer?.contains(active))) {
        e.preventDefault()
        last.focus()
      } else if (!e.shiftKey && active === last) {
        e.preventDefault()
        first.focus()
      }
    }
    document.addEventListener("keydown", onKey)
    return () => {
      window.cancelAnimationFrame(handle)
      document.removeEventListener("keydown", onKey)
      // Back to the control that opened it, so the thumb and the caret agree.
      trigger?.focus()
    }
  }, [mobileOpen])

  // The drawer is a route-scale overlay: a navigation closes it.
  useEffect(() => {
    setMobileOpen(false)
  }, [location.pathname])

  // PRD-20 v1.3 §FR-49 — one global listener for grading auto-commit events.
  // When the worker finishes scoring, every consumer surface (material results,
  // student pages, /materiales/:id, roster pill, home KPIs) refreshes.
  useGradingCompletionInvalidator()

  if (!professor) return <Outlet />

  const hideShellFab = ROUTES_WITHOUT_SHELL_FAB.some((pattern) => pattern.test(location.pathname))

  return (
    // PRD-28 — <FeedbackProvider> wraps the authenticated tree so any surface
    // can call useFeedback().openFeedback().
    // Fixed app frame: the sidebar is always visible; only <main> scrolls.
    // `fixed inset-0` keeps document-level scroll from moving the shell itself
    // and exposing the page background underneath.
    <FeedbackProvider>
      <div className="bg-canvas fixed inset-0 flex overflow-hidden">
        <div className="hidden h-full md:flex">
          <Sidebar professor={professor} classrooms={classrooms} onLogout={handleLogout} />
        </div>

        {mobileOpen && (
          <>
            <div
              className="bg-overlay fixed inset-0 z-40 md:hidden"
              onClick={closeMobile}
              aria-hidden="true"
            />
            <div
              ref={drawerRef}
              role="dialog"
              aria-modal="true"
              aria-label={t("nav.aria.primary")}
              className="fixed inset-y-0 left-0 z-50 md:hidden"
            >
              <Sidebar
                professor={professor}
                classrooms={classrooms}
                onLogout={handleLogout}
                onCloseMobile={closeMobile}
                showCloseButton
              />
            </div>
          </>
        )}

        <div className="flex h-full min-w-0 flex-1 flex-col">
          {/* Dev-only banner. `import.meta.env.DEV` is true under `vite dev`
              and false under `vite build`, so this branch is tree-shaken out of
              production bundles entirely. */}
          {import.meta.env.DEV && (
            <div
              role="status"
              aria-live="polite"
              className="bg-attention-soft text-attention-ink border-hairline shrink-0 border-b px-4 py-1.5 text-center text-xs font-semibold tracking-wide"
            >
              {t("dev.banner")}
            </div>
          )}
          {/* PRD-22 — persistent banner for internal-domain professors when
              INTERNAL_ACCOUNT_EMAILS_ENABLED=False. Renders null for the
              dominant case (external teachers) so no DOM cost. */}
          <InternalAccountBanner />
          <header className="border-border bg-surface flex shrink-0 items-center gap-2 border-b px-4 py-2 md:hidden">
            <button
              ref={hamburgerRef}
              type="button"
              onClick={() => setMobileOpen(true)}
              aria-label={t("nav.aria.open_menu")}
              aria-expanded={mobileOpen}
              className="rounded-control text-ink-muted hover:bg-inset flex h-11 w-11 items-center justify-center"
            >
              <Menu className="h-5 w-5" aria-hidden />
            </button>
            {/* Same mark as the desktop sidebar header — one component, so the
                two breakpoints cannot drift. */}
            <SchoolMark
              logoUrl={professor?.school?.logo_url}
              schoolName={professor?.school?.name}
              fallbackName={t("app.name")}
              compact
              className="min-w-0"
            />
          </header>
          <main ref={mainRef} className="min-h-0 flex-1 overflow-x-hidden overflow-y-auto">
            <Outlet />
          </main>
        </div>

        {/* PRD-01 §FR-19 — the product's primary action, one tap from anywhere
            on a phone. `z-30` keeps it under the drawer scrim (z-40) so it
            disappears while the menu is open, and it stands down entirely where
            the page needs this slot (see ROUTES_WITHOUT_SHELL_FAB). */}
        {!hideShellFab && (
          <Button
            onClick={() => navigate("/materiales?new=1")}
            leftIcon={<Sparkles className="h-5 w-5" />}
            size="lg"
            className="fixed right-4 bottom-4 z-30 h-14 w-14 rounded-full p-0 shadow-lg md:hidden"
            aria-label={t("nav.generate_material")}
          />
        )}
      </div>
    </FeedbackProvider>
  )
}
